import { useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';

const FullscreenWrapper = styled.div`
  height: 100%;
  width: 100%;
  background: black;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  /* Ensure safe area padding on iOS */
  padding-bottom: env(safe-area-inset-bottom);
`;

const Header = styled.div`
  height: 56px;
  width: 100%;
  background: black;
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 18px;
  padding-top: max(0px, env(safe-area-inset-top));
  min-height: calc(56px + env(safe-area-inset-top));
  font-size: 16px;
  font-weight: 500;
  border-bottom: 1px solid #222;
  flex-shrink: 0;
`;

const HeaderButton = styled.button`
  color: white;
  font-size: 16px;
  cursor: pointer;
  font-weight: 500;
  background: none;
  border: none;
  padding: 0;
  &:hover {
    opacity: 0.7;
  }
`;

const CropperViewport = styled.div`
  flex: 1;
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  /* Critical: allow pinch + dragging in Cropper */
  touch-action: pan-x pan-y pinch-zoom;
  -ms-touch-action: pan-x pan-y pinch-zoom;
  overflow: hidden;

  .cropper-container {
    width: 100% !important;
    height: 100% !important;
    touch-action: pan-x pan-y pinch-zoom;
    -ms-touch-action: pan-x pan-y pinch-zoom;
  }

  .cropper-canvas {
    max-width: 100% !important;
    max-height: 100% !important;
  }

  .cropper-image {
    /* Ensure smooth image movement */
    will-change: transform;
    transform-origin: center center;
    /* Improve touch responsiveness on mobile */
    touch-action: none;
    -ms-touch-action: none;
  }

  .cropper-view-box {
    /* Smooth crop box rendering */
    outline: 1px solid rgba(255, 255, 255, 0.5);
    outline-offset: -1px;
    /* Allow dragging within the crop box */
    cursor: move;
  }

  .cropper-face {
    /* Allow dragging on the crop box face */
    cursor: move;
    touch-action: none;
    -ms-touch-action: none;
  }

  .cropper-line,
  .cropper-point {
    /* Keep lines and points non-interactive to avoid confusion */
    pointer-events: none;
  }

  .cropper-drag-box {
    /* Allow dragging on the entire cropper area */
    cursor: move;
    touch-action: none;
    -ms-touch-action: none;
  }
`;

const BleedingOverlay = styled.div`
  position: absolute;
  top: ${({ top }) => top}px;
  left: ${({ left }) => left}px;
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  pointer-events: none;
  z-index: 10;

  &::before {
    content: '';
    position: absolute;
    top: ${({ bleedingPercent, height }) => height * bleedingPercent}px;
    left: ${({ bleedingPercent, width }) => width * bleedingPercent}px;
    right: ${({ bleedingPercent, width }) => width * bleedingPercent}px;
    bottom: ${({ bleedingPercent, height }) => height * bleedingPercent}px;
    border: 2px dashed rgba(255, 0, 0, 0.6);
    box-sizing: border-box;
  }

  &::after {
    content: 'Safe Area';
    position: absolute;
    top: ${({ bleedingPercent, height }) => height * bleedingPercent + 5}px;
    left: ${({ bleedingPercent, width }) => width * bleedingPercent + 5}px;
    background: rgba(255, 0, 0, 0.8);
    color: white;
    padding: 2px 6px;
    font-size: 10px;
    border-radius: 3px;
    font-weight: 600;
  }
`;

const CardImageCropper = ({
  image,
  isPhysical = false,
  onCancel,
  onComplete,
}) => {
  const theme = useTheme();
  const cropperRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [overlayPosition, setOverlayPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });

  // Aspect ratios (same as CustomizeCardImageUploader)
  const digitalAspectRatio = 407 / 226;
  const physicalAspectRatio = 85.6 / 53.98;
  const aspectRatio = isPhysical ? physicalAspectRatio : digitalAspectRatio;

  // Safe-area bleeding
  const bleedingArea = isPhysical ? 0.04 : 0;

  // ──────────────────────────
  // Overlay update logic
  // ──────────────────────────
  const updateOverlay = () => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper || !isPhysical) return;

    const box = cropper.getCropBoxData();
    setOverlayPosition({
      top: box.top,
      left: box.left,
      width: box.width,
      height: box.height,
    });
  };

  const handleReady = () => {
    updateOverlay();
  };

  const handleMove = () => {
    updateOverlay();
  };

  // ──────────────────────────
  // Cropping output
  // ──────────────────────────
  const handleNext = async () => {
    if (!onComplete) return;

    setLoading(true);
    try {
      const cropper = cropperRef.current?.cropper;
      if (!cropper) return;

      const croppedDataUrl = cropper.getCroppedCanvas().toDataURL();
      await onComplete(croppedDataUrl);
    } finally {
      setLoading(false);
    }
  };

  if (!image) {
    return null;
  }

  return (
    <FullscreenWrapper>
      {/* HEADER */}
      <Header>
        <HeaderButton onClick={onCancel}>✕</HeaderButton>
        <div>Customize Card</div>
        <HeaderButton onClick={handleNext} disabled={loading}>
          {loading ? 'Processing...' : 'Next'}
        </HeaderButton>
      </Header>

      {/* CROPPER */}
      <CropperViewport>
        {isPhysical && overlayPosition.width > 0 && (
          <BleedingOverlay
            top={overlayPosition.top}
            left={overlayPosition.left}
            width={overlayPosition.width}
            height={overlayPosition.height}
            bleedingPercent={bleedingArea}
          />
        )}
        <Cropper
          ref={cropperRef}
          src={image}
          aspectRatio={aspectRatio}
          viewMode={1}
          dragMode="move"
          minCropBoxHeight={10}
          minCropBoxWidth={10}
          autoCropArea={1}
          background={false}
          responsive={true}
          checkOrientation={false}
          guides={true}
          movable={true}
          scalable={true}
          zoomable={true}
          zoomOnTouch={true}
          zoomOnWheel={true}
          cropBoxMovable={false}
          cropBoxResizable={false}
          toggleDragModeOnDblclick={false}
          restore={false}
          ready={handleReady}
          crop={handleMove}
          zoom={handleMove}
          move={handleMove}
          style={{
            height: '100%',
            width: '100%',
            maxWidth: '100%',
            maxHeight: '100%'
          }}
        />
      </CropperViewport>
    </FullscreenWrapper>
  );
};

export default CardImageCropper;
